// ============================================================
// ONBOARDING TOUR — 初回オンボーディングガイド（ようこそモーダル + 数ステップ）。
//
// 非エンジニアのメンバー向けに、ポータルの主要機能を一言ずつ紹介する。
//   • 初回ログイン時に1回だけ自動表示（lib/onboarding.ts の viewer 単位フラグ）
//   • TopBar の「ガイド」導線からいつでも再表示できる
// 文言は専門用語を避ける（「議事録 = 会議のメモ」「AIが自動で」等）。
//
// アクセシビリティ：
//   • Esc で閉じる（= スキップ扱い・既読化）
//   • 開いたら「次へ」ボタンへ初期フォーカス、背景スクロールを止める
//   • role="dialog" aria-modal + ステップ見出しを aria-labelledby で紐付け
// バックエンド不要（localStorage のみ・既存挙動を壊さない純UI層）。
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icons';
import { useAuth } from '../auth/AuthContext';
import { markOnboarded } from '../lib/onboarding';

interface Step {
  /** Icons.tsx のアイコン名。 */
  icon: string;
  /** アクセント色（アイコン背景・チップ）。 */
  color: string;
  /** ステップ見出し（短く）。 */
  title: string;
  /** 1〜2文の説明（専門用語を避ける）。 */
  body: string;
  /** 任意の補足（「画面のどこにあるか」等の道案内）。 */
  hint?: string;
}

// 司令塔メモ：文言は非エンジニア前提で「何ができるか」を一言で。
// 機能名はサイドバー/トップバーの実ラベルと揃える（議事録・マイタスク・カレンダー・通知ベル）。
const STEPS: Step[] = [
  {
    icon: 'sparkles',
    color: '#F59E0B',
    title: 'TEMP 社内ポータルへようこそ',
    body: 'ここは、会議のメモ・自分のタスク・予定・ナレッジを 1か所でまとめて見られる場所です。まずは何ができるか、かんたんにご案内します。',
    hint: '所要 約30秒・いつでもスキップできます',
  },
  {
    icon: 'file-text',
    color: '#0EA5E9',
    title: '議事録（会議のメモ）',
    body: '会議の文字起こしや Word・PDF のメモを貼りつけると、AI が読みやすい議事録に整理し、あなたのやることリストまで自動で作ります。',
    hint: '左メニューの「アップロード」から貼りつけられます',
  },
  {
    icon: 'list-checks',
    color: '#6366F1',
    title: 'マイタスク（自分のやること）',
    body: '自分に割りあてられたタスクが一覧で並びます。終わったものはチェックして完了にできます。',
    hint: '左メニューの「マイタスク」',
  },
  {
    icon: 'calendar-days',
    color: '#10B981',
    title: 'カレンダー（自分の予定）',
    body: '自分の予定をまとめて確認できます。Google カレンダーとつなぐと、会議や締め切りもここに表示されます。',
    hint: '左メニューの「カレンダー」',
  },
  {
    icon: 'bell',
    color: '#B45309',
    title: '通知ベル 🔔（あなた宛の新着）',
    body: '右上のベルを押すと、あなた宛の新着がまとまって出ます。新しいタスク・参加した会議の議事録が届きます。',
    hint: '画面の右上にあります',
  },
  {
    icon: 'life-buoy',
    color: '#0369A1',
    title: '準備はOKです',
    body: 'まずはホーム画面から、今日の予定とタスクをのぞいてみてください。困ったときは、右上の「ガイド」からいつでもこの案内をもう一度見られます。それでも分からなければ溝邊さんに聞いてください。',
  },
];

export default function OnboardingTour({ onClose }: { onClose: () => void }) {
  // 既読化は Auth uid（ユーザー単位）で記録する（端末をまたいで1回だけになる）。
  const { uid } = useAuth();
  const [step, setStep] = useState(0);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  const last = STEPS.length - 1;
  const isFirst = step === 0;
  const isLast = step === last;
  const current = STEPS[step];

  // 閉じる＝既読化（自動でも手動でも一度見たら次回から自動表示しない）。
  // markOnboarded は Firestore 書込（async）だが UI を待たせる必要はない＝発火だけして閉じる。
  // uid 無し（未ログイン）では記録できないので閉じるだけ。
  const finish = () => {
    if (uid) void markOnboarded(uid);
    onClose();
  };

  const next = () => {
    if (isLast) finish();
    else setStep((s) => Math.min(s + 1, last));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  // Esc で閉じる + 背景スクロール固定（モーダル中の最低限の作法）。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // finish は uid / onClose に依存するが、Esc 購読は1回でよい（毎レンダで張り直さない）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ステップが変わるたびに主ボタンへフォーカス（キーボード操作を妨げない）。
  useEffect(() => {
    primaryRef.current?.focus();
  }, [step]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="presentation"
    >
      {/* 背景オーバーレイ — クリックで閉じる（スキップ扱い） */}
      <div
        className="absolute inset-0 bg-charcoal/55 backdrop-blur-sm"
        onClick={finish}
        aria-hidden="true"
      />

      {/* ダイアログ本体 */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        className="relative w-full sm:max-w-md rounded-t-card sm:rounded-card border border-hair bg-white shadow-xl overflow-hidden fade-up"
      >
        {/* ヘッダー：ステップ番号 + 閉じる */}
        <div className="flex items-center justify-between gap-3 px-5 pt-4">
          <span className="text-2xs font-bold tracking-wide text-slate-sub">
            はじめてガイド
            <span className="font-num text-slate-400">
              {' '}
              {step + 1} / {STEPS.length}
            </span>
          </span>
          <button
            type="button"
            onClick={finish}
            aria-label="ガイドを閉じる"
            className="rounded-soft p-1.5 text-slate-400 hover:text-charcoal hover:bg-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-brand/40"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* 本文 */}
        <div className="px-5 pt-3 pb-5">
          <span
            className="grid place-items-center w-12 h-12 rounded-card"
            style={{ background: current.color + '1f', color: current.color }}
          >
            <Icon name={current.icon} size={24} />
          </span>
          <h2 id="onboarding-title" className="mt-3 text-lg font-bold text-ink">
            {current.title}
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-charcoal">{current.body}</p>
          {current.hint && (
            <p className="mt-2.5 inline-flex items-center gap-1.5 rounded-soft bg-base px-2.5 py-1 text-2xs text-slate-sub">
              <Icon name="info" size={13} className="text-slate-400" />
              {current.hint}
            </p>
          )}
        </div>

        {/* 進捗ドット */}
        <div className="px-5 pb-2 flex items-center justify-center gap-1.5" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-5 bg-amber-brand' : 'w-1.5 bg-hair'
              }`}
            />
          ))}
        </div>

        {/* フッター：戻る / スキップ / 次へ・始める */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-hair">
          {isFirst ? (
            <button
              type="button"
              onClick={finish}
              className="text-xs font-medium text-slate-sub hover:text-charcoal px-2 py-2"
            >
              スキップ
            </button>
          ) : (
            <button
              type="button"
              onClick={back}
              className="inline-flex items-center gap-1 rounded-soft border border-hair bg-white px-3 py-2 text-sm text-charcoal hover:bg-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-brand/40"
            >
              <Icon name="chevron-left" size={15} />
              戻る
            </button>
          )}

          <button
            ref={primaryRef}
            type="button"
            onClick={next}
            className="ml-auto inline-flex items-center gap-1.5 rounded-soft bg-charcoal px-4 py-2 text-sm font-semibold text-white hover:bg-charcoal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-brand/50"
          >
            {isLast ? (
              <>
                <Icon name="check" size={16} />
                始める
              </>
            ) : (
              <>
                次へ
                <Icon name="chevron-right" size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
